import 'react-datepicker/dist/react-datepicker.css';

import { useQuery } from '@tanstack/react-query';
import moment from 'moment';
import React, { useMemo, useRef } from 'react';
import DatePicker from 'react-datepicker';
import { useTranslation } from 'react-i18next';
import { FaCalendarAlt, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { MdRefresh } from 'react-icons/md';
import styled from 'styled-components';

import LoadingImage from '../../components/general/LoadingImage';
import { regionToCategory } from '../../components/stock/stockCategory';
import { uiConstants } from '../../constants';
import { uiHelpers } from '../../helpers';
import useRegion from '../../hooks/useRegion';
import { dailypriceService } from '../../services';
import FeatureRequiresVIPTooltip from '../FreeUser/FeatureRequiresVIPTooltip';
import TutorialQuestionMark from '../Help/TutorialQuestionMark';

const DatePickerContainer = styled.div`
  display: inline-block;
  input {
    display: none;
  }
`;

interface Props {
  hasDatePicker?: boolean;
  datePickerHoverText?: string;
  dateChanged?: (date: Date) => void;
  when?: string;
  updatedAt?: string | number;
  loading?: boolean;
  hasRefresh?: boolean;
}

const UpdatedAtLabel: React.FC<Props> = ({
  hasDatePicker,
  datePickerHoverText,
  updatedAt,
  when,
  hasRefresh,
  dateChanged,
  loading,
}) => {
  const datePickerRef = useRef();
  const { t } = useTranslation();
  const region = useRegion();
  const category = regionToCategory(region);
  let date;
  try {
    date = moment(when, uiConstants.DATE_FORMAT_AWS).toDate();
  } catch {
    date = moment(when, uiConstants.DATE_FORMAT).toDate();
  }
  const { data: availableDate } = useQuery(['statisticsHistory'], () =>
    dailypriceService.statisticsHistory(category)
  );

  const datePicker = useMemo(() => {
    return hasDatePicker === true && dateChanged ? (
      <FeatureRequiresVIPTooltip
        featureTitle={t('feature-date-navigator')}
        tooltipTextForVIP={datePickerHoverText}
      >
        <span>
          <DatePickerContainer>
            <DatePicker
              ref={datePickerRef}
              selected={date}
              onChange={dateChanged}
              includeDates={
                availableDate
                  ? availableDate.map(date => moment(date).toDate())
                  : []
              }
              dateFormat={uiConstants.DATE_FORMAT_DATEPICKER}
            />
          </DatePickerContainer>
          <FaChevronLeft
            style={{ cursor: 'pointer' }}
            onClick={() => {
              dateChanged(moment(date).subtract(1, 'day').toDate());
            }}
          />
          <FaCalendarAlt
            style={{ cursor: 'pointer' }}
            onClick={() => {
              const picker = datePickerRef.current as any;
              if (picker) picker.input.click();
            }}
          />
          <FaChevronRight
            style={{ cursor: 'pointer' }}
            onClick={() => {
              dateChanged(moment(date).add(1, 'day').toDate());
            }}
          />
          <TutorialQuestionMark tutorialId={22} />
        </span>
      </FeatureRequiresVIPTooltip>
    ) : (
      <></>
    );
  }, [hasDatePicker, dateChanged, t, datePickerHoverText, date, availableDate]);

  return (
    <div
      className="text-center"
      style={{
        fontSize: '0.8rem',
        color: 'gray',
        marginBottom: '0.5rem',
        marginTop: '0.5rem',
      }}
    >
      <LoadingImage displayAsBlock small loading={loading} />
      {updatedAt && (
        <span>
          {t('Updated at')}{' '}
          {typeof updatedAt === 'string'
            ? uiHelpers.formatDateTime(moment.utc(updatedAt).toDate())
            : moment.unix(updatedAt).format(uiConstants.DATETIME_FORMAT)}
          ,&nbsp;
        </span>
      )}
      {when && (
        <span>
          {t('showing data of %s').split('%s')[0]}{' '}
          {moment(date).format(uiConstants.DATE_FORMAT)} {datePicker}{' '}
          {t('showing data of %s').split('%s')[1]}
        </span>
      )}
      {hasRefresh && (
        <MdRefresh
          style={{ cursor: 'pointer' }}
          onClick={() => dateChanged(date)}
        />
      )}
    </div>
  );
};

export default UpdatedAtLabel;
